import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Upload, X, Check, Save, Star, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import {
  getMarketplaceProfile,
  createListing,
  updateListing,
  getListingDetail,
  getMarketplaceCategories,
  ListingType,
  ListingStatus,
  PriceUnit,
  MarketplaceCategory,
  CreateListingPayload,
  MarketplaceProfile,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { uploadToCloudinary1 } from "../../../Redux/ApiCalls/Cloudinary";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import BrandDatePicker from "../../atoms/Utlis/BrandDatePicker";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import MarketplaceLoader from "./MarketplaceLoader";
import { isProfileCompleted } from "./MarketplaceShared";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

interface FormState {
  listing_type: ListingType;
  title: string;
  description: string;
  category: string;
  price: string;
  price_unit: PriceUnit;
  location: string;
  status: ListingStatus;
  image_url: string;
  image_urls: string[];
  metadata_brand: string;
  metadata_model: string;
  available_from: string;
  available_to: string;
}

const defaultForm: FormState = {
  listing_type: "sale",
  title: "",
  description: "",
  category: "",
  price: "",
  price_unit: "fixed",
  location: "",
  status: "active",
  image_url: "",
  image_urls: [],
  metadata_brand: "",
  metadata_model: "",
  available_from: "",
  available_to: "",
};

const MarketplaceAddListingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEdit = Boolean(editId);

  const [form, setForm] = useState<FormState>(defaultForm);
  const [categories, setCategories] = useState<MarketplaceCategory[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [authChecking, setAuthChecking] = useState(true);
  const [myProfile, setMyProfile] = useState<MarketplaceProfile | null>(null);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [previewModalIndex, setPreviewModalIndex] = useState<number | null>(null);

  const openPreview = (index: number) => {
    setPreviewModalIndex(index);
  };

  const closePreview = () => {
    setPreviewModalIndex(null);
  };

  const showNextPreview = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (previewModalIndex === null || form.image_urls.length === 0) return;
    setPreviewModalIndex((prev) => (prev !== null ? (prev + 1) % form.image_urls.length : 0));
  };

  const showPrevPreview = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (previewModalIndex === null || form.image_urls.length === 0) return;
    setPreviewModalIndex((prev) => (prev !== null ? (prev - 1 + form.image_urls.length) % form.image_urls.length : 0));
  };

  useEffect(() => {
    if (previewModalIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePreview();
      if (e.key === "ArrowRight") showNextPreview();
      if (e.key === "ArrowLeft") showPrevPreview();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewModalIndex, form.image_urls.length]);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/marketplace");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      }, 100);
      return;
    }
    getMarketplaceProfile()
      .then((res) => {
        const profileData = res?.data || null;
        if (!isProfileCompleted(profileData)) {
          navigate("/marketplace/profile/create");
        } else {
          setMyProfile(profileData);
          localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
          setAuthChecking(false);
        }
      })
      .catch(() => {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const prof = parsed?.data || parsed;
            if (isProfileCompleted(prof)) {
              setMyProfile(prof);
              setAuthChecking(false);
              return;
            }
          } catch {}
        }
        navigate("/marketplace/profile/create");
      });
  }, []);

  useEffect(() => {
    if (authChecking) return;
    getMarketplaceCategories().then(setCategories).catch(() => {});
    if (isEdit && editId) {
      setLoading(true);
      getListingDetail(editId)
        .then((data) => {
          const rawUrls = data.metadata?.image_urls || data.metadata?.images;
          let parsedUrls: string[] = [];
          if (Array.isArray(rawUrls)) {
            parsedUrls = rawUrls;
          } else if (typeof rawUrls === "string") {
            try {
              const p = JSON.parse(rawUrls);
              if (Array.isArray(p)) parsedUrls = p;
              else if (rawUrls.includes(",")) parsedUrls = rawUrls.split(",").map(u => u.trim());
              else parsedUrls = [rawUrls];
            } catch {
              parsedUrls = rawUrls.includes(",") ? rawUrls.split(",").map(u => u.trim()) : [rawUrls];
            }
          }
          let loadedUrls = parsedUrls.filter(Boolean);
          if (data.image_url) {
            if (!loadedUrls.includes(data.image_url)) {
              loadedUrls = [data.image_url, ...loadedUrls];
            } else if (loadedUrls[0] !== data.image_url) {
              loadedUrls = [data.image_url, ...loadedUrls.filter(u => u !== data.image_url)];
            }
          }
          if (loadedUrls.length === 0 && data.image_url) {
            loadedUrls = [data.image_url];
          }

          setForm({
            listing_type: data.listing_type,
            title: data.title,
            description: data.description || "",
            category: String(data.category || ""),
            price: data.price,
            price_unit: data.price_unit,
            location: data.location || "",
            status: data.status,
            image_url: data.image_url || loadedUrls[0] || "",
            image_urls: loadedUrls,
            metadata_brand: data.metadata?.brand || "",
            metadata_model: data.metadata?.model || "",
            available_from: data.available_from ? data.available_from.substring(0, 10) : "",
            available_to: data.available_to ? data.available_to.substring(0, 10) : "",
          });
        })
        .catch(() => showErrorToast("Failed to load listing"))
        .finally(() => setLoading(false));
    }
  }, [authChecking, editId]);

  const set = (key: keyof FormState, val: string) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const isEquipmentType =
    form.listing_type === "sale" || form.listing_type === "rent";

  const getDaysCount = (fromStr: string, toStr: string) => {
    if (!fromStr || !toStr) return 0;
    const fromDate = new Date(fromStr);
    const toDate = new Date(toStr);
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return 0;
    const diffTime = toDate.getTime() - fromDate.getTime();
    if (diffTime < 0) return 0;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const setImageUrls = (urlsOrFn: string[] | ((prevUrls: string[]) => string[])) => {
    setForm((prev) => {
      const nextUrls = typeof urlsOrFn === "function" ? urlsOrFn(prev.image_urls) : urlsOrFn;
      return {
        ...prev,
        image_urls: nextUrls,
        image_url: nextUrls[0] || "",
      };
    });
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${index}`);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setImageUrls((prevUrls) => {
      const updated = [...prevUrls];
      const [movedItem] = updated.splice(draggedIndex, 1);
      updated.splice(targetIndex, 0, movedItem);
      return updated;
    });

    if (targetIndex === 0 || draggedIndex === 0) {
      showSuccessToast("Primary image updated!");
    } else {
      showSuccessToast("Images reordered!");
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (imageUploading) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) {
      e.target.value = "";
      return;
    }

    const currentCount = form.image_urls.length;
    const remainingSlots = 5 - currentCount;

    if (remainingSlots <= 0) {
      showErrorToast("Maximum 5 images allowed");
      e.target.value = "";
      return;
    }

    const isExceeding = files.length > remainingSlots;
    const filesToUpload = isExceeding ? files.slice(0, remainingSlots) : files;

    setImageUploading(true);
    try {
      const urls: string[] = [];
      for (const file of filesToUpload) {
        urls.push(await uploadToCloudinary1(file));
      }
      setImageUrls((prevUrls) => [...prevUrls, ...urls]);

      if (isExceeding) {
        showSuccessToast(
          remainingSlots === 5
            ? "You can upload max 5 images. First 5 images are uploaded"
            : `You can upload max 5 images. First ${remainingSlots} images are uploaded`
        );
      } else {
        showSuccessToast(filesToUpload.length === 1 ? "Image uploaded" : "Images uploaded");
      }
    } catch {
      showErrorToast("Image upload failed");
    } finally {
      setImageUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url: string) => {
    setImageUrls((prevUrls) => prevUrls.filter((item) => item !== url));
  };

  const handleSubmit = async (e: React.FormEvent, customStatus?: ListingStatus) => {
    if (e) e.preventDefault();
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot save listings.");
      return;
    }
    if (!form.title.trim()) {
      showErrorToast("Title is required");
      return;
    }
    if (form.image_urls.length === 0) {
      showErrorToast("Please upload at least one listing image");
      return;
    }
    setSubmitting(true);
    try {
      const finalStatus = customStatus || form.status;
      const payload: CreateListingPayload = {
        listing_type: form.listing_type,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        category: form.category ? Number(form.category) : undefined,
        price: form.price || undefined,
        price_unit: form.price_unit,
        location: form.location.trim() || undefined,
        status: finalStatus,
        image_url: form.image_urls[0] || undefined,
        available_from: form.listing_type === "rent" && form.available_from ? form.available_from : undefined,
        available_to: form.listing_type === "rent" && form.available_to ? form.available_to : undefined,
      };
      const metadata: Record<string, any> = {
        image_urls: form.image_urls,
      };
      if (isEquipmentType) {
        if (form.metadata_brand) metadata.brand = form.metadata_brand;
        if (form.metadata_model) metadata.model = form.metadata_model;
      }
      if (Object.keys(metadata).length > 0) {
        payload.metadata = metadata;
      }
      if (isEdit && editId) {
        await updateListing(editId, payload);
        sendFirebaseEvent('Marketplace_Listing_Updated', 'Marketplace', form.listing_type, editId);
        showSuccessToast("Listing updated!");
      } else {
        await createListing(payload);
        sendFirebaseEvent('Marketplace_Listing_Created', 'Marketplace', form.listing_type, form.price || 'no_price');
        showSuccessToast("Listing created!");
      }
      navigate("/marketplace/my-listings");
    } catch (err: any) {
      showErrorToast(
        err?.response?.data?.message || "Failed to save listing"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Listing health calculation
  const getListingHealth = () => {
    let health = 0;
    if (form.image_urls.length > 0) health += 30;
    if (form.image_urls.length >= 3) health += 10;
    if (form.title.trim()) health += 15;
    if (form.metadata_brand.trim() && form.metadata_model.trim()) health += 15;
    if (form.category) health += 10;
    if (form.location.trim()) health += 10;
    if (form.description.trim().length > 15) health += 10;
    return health;
  };

  const healthScore = getListingHealth();

  if (authChecking || loading) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <PageWrap>
      <Container>
        <LayoutGrid>
          {/* Sidebar */}
          <SidebarColumn>
            <OnboardingLabel>STEP 1 OF 1</OnboardingLabel>
            <PageTitle>{isEdit ? "Edit Your Gear" : "List Your Gear"}</PageTitle>
            <PageSubtitle>
              Reach 50k+ professional cinematographers and photographers worldwide.
            </PageSubtitle>

            <HealthCard>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <HealthTitle>Listing Health</HealthTitle>
                <HealthPercent>{healthScore}%</HealthPercent>
              </div>
              <HealthBarOuter>
                <HealthBarInner $percent={healthScore} />
              </HealthBarOuter>

              <HealthTasksList>
                <TaskItem $done={form.image_urls.length >= 1}>
                  <TaskIconWrap $done={form.image_urls.length >= 1}>
                    {form.image_urls.length >= 1 ? <Check size={10} /> : <div style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />}
                  </TaskIconWrap>
                  <span>Add 1+ high-res photos</span>
                </TaskItem>
                <TaskItem $done={Boolean(form.metadata_brand.trim() && form.metadata_model.trim() && form.category)}>
                  <TaskIconWrap $done={Boolean(form.metadata_brand.trim() && form.metadata_model.trim() && form.category)}>
                    {Boolean(form.metadata_brand.trim() && form.metadata_model.trim() && form.category) ? <Check size={10} /> : <div style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />}
                  </TaskIconWrap>
                  <span>Detail technical specs</span>
                </TaskItem>
                <TaskItem $done={form.description.trim().length > 15}>
                  <TaskIconWrap $done={form.description.trim().length > 15}>
                    {form.description.trim().length > 15 ? <Check size={10} /> : <div style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />}
                  </TaskIconWrap>
                  <span>Write a descriptive bio</span>
                </TaskItem>
              </HealthTasksList>
            </HealthCard>
          </SidebarColumn>

          {/* Form Content */}
          <FormColumn onSubmit={(e) => handleSubmit(e)}>
            {myProfile?.is_active === false && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 18px",
                  marginBottom: "20px",
                  borderRadius: "10px",
                  background: "rgba(239, 68, 68, 0.12)",
                  border: "1px solid rgba(239, 68, 68, 0.35)",
                  color: "#fca5a5",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                <AlertTriangle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
                <div>
                  <strong>Account Frozen:</strong> Your marketplace account has been deactivated/frozen by an administrator.
                  You cannot create new listings or edit listings.
                </div>
              </div>
            )}
            
            {/* Visual Gallery */}
            <FormCard>
              <CardHeader>
                <CardStepNum>01</CardStepNum>
                <CardSectionName>Visual Gallery ({form.image_urls.length}/5)</CardSectionName>
              </CardHeader>

              <ImageUploadArea>
                {form.image_urls.length > 0 ? (
                  <>
                    <GalleryUploadGrid>
                      {/* Primary Image Slot (#1) */}
                      <PrimaryImageSlot
                        draggable
                        onDragStart={(e) => handleDragStart(e, 0)}
                        onDragOver={(e) => handleDragOver(e, 0)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, 0)}
                        onDragEnd={handleDragEnd}
                        onClick={() => openPreview(0)}
                        $isDragging={draggedIndex === 0}
                        $isDragOver={dragOverIndex === 0 && draggedIndex !== 0}
                        title="Click to preview • Drag to reorder"
                      >
                        <PreviewImg src={form.image_urls[0]} alt="Primary" />
                        <RemoveImgBtn
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(form.image_urls[0]);
                          }}
                          title="Remove photo"
                        >
                          <X size={14} />
                        </RemoveImgBtn>
                        <PrimaryBadge>
                          <Star size={11} fill="#ffffff" color="#ffffff" />
                          <span>#1 Primary</span>
                        </PrimaryBadge>
                      </PrimaryImageSlot>

                      {/* Secondary Slots (4 slots maximum) */}
                      <SecondarySlotsGrid>
                        {form.image_urls.slice(1).map((url, idx) => {
                          const actualIndex = idx + 1;
                          return (
                            <SecondaryImageSlot
                              key={url}
                              draggable
                              onDragStart={(e) => handleDragStart(e, actualIndex)}
                              onDragOver={(e) => handleDragOver(e, actualIndex)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, actualIndex)}
                              onDragEnd={handleDragEnd}
                              onClick={() => openPreview(actualIndex)}
                              $isDragging={draggedIndex === actualIndex}
                              $isDragOver={dragOverIndex === actualIndex && draggedIndex !== actualIndex}
                              title="Click to preview • Drag to reorder"
                            >
                              <PreviewImg src={url} alt={`Photo ${actualIndex + 1}`} />
                              <RemoveImgBtn
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeImage(url);
                                }}
                                title="Remove photo"
                              >
                                <X size={12} />
                              </RemoveImgBtn>
                              <NumberBadge>#{actualIndex + 1}</NumberBadge>
                            </SecondaryImageSlot>
                          );
                        })}

                        {form.image_urls.length < 5 && (
                          <SecondaryUploadPlaceholder onClick={() => document.getElementById("listing-img-upload")?.click()}>
                            <Upload size={16} />
                            <span>+ Add</span>
                          </SecondaryUploadPlaceholder>
                        )}
                      </SecondarySlotsGrid>
                    </GalleryUploadGrid>

                    <GalleryHintText>
                      💡 Tap any photo to preview full size. Drag and drop photos to reorder / change <strong>#1 Primary</strong>.
                    </GalleryHintText>
                  </>
                ) : (
                  <UploadPlaceholderBox onClick={() => document.getElementById("listing-img-upload")?.click()}>
                    <UploadIconWrap>
                      <Upload size={20} />
                    </UploadIconWrap>
                    <UploadBoxTitle>Upload Primary Image</UploadBoxTitle>
                    <UploadBoxSubtitle>Drag and drop or click to upload</UploadBoxSubtitle>
                  </UploadPlaceholderBox>
                )}

                <input
                  id="listing-img-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
              </ImageUploadArea>
              {imageUploading && <LoadingText>Uploading listing photo...</LoadingText>}
            </FormCard>

            {/* Equipment Details */}
            <FormCard>
              <CardHeader>
                <CardStepNum>02</CardStepNum>
                <CardSectionName>Equipment Details</CardSectionName>
              </CardHeader>
              
              <FormGroup>
                <Label>Equipment Title *</Label>
                <Input
                  required
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. Sony 24-70mm f/2.8 Lens Rental"
                />
              </FormGroup>

              <ThreeCol>
                <FormGroup>
                  <Label>Category</Label>
                  <Select
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Brand</Label>
                  <Input
                    value={form.metadata_brand}
                    onChange={(e) => set("metadata_brand", e.target.value)}
                    placeholder="e.g. Sony, Canon"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Model</Label>
                  <Input
                    value={form.metadata_model}
                    onChange={(e) => set("metadata_model", e.target.value)}
                    placeholder="e.g. A7 IV"
                  />
                </FormGroup>
              </ThreeCol>
            </FormCard>

            {/* Pricing & Availability */}
            <FormCard>
              <CardHeader>
                <CardStepNum>03</CardStepNum>
                <CardSectionName>Pricing & Availability</CardSectionName>
              </CardHeader>

              <PricingTogglesGrid>
                {/* Rent Option */}
                <PriceOptionBox 
                  $active={form.listing_type === "rent"}
                  onClick={() => set("listing_type", "rent")}
                >
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <input 
                      type="radio" 
                      checked={form.listing_type === "rent"} 
                      onChange={() => set("listing_type", "rent")}
                      style={{ accentColor: "#c94daa" }}
                    />
                    <strong>🔑 Rent Out Gear</strong>
                  </div>
                  {form.listing_type === "rent" && (
                    <OptionFieldsWrap onClick={e => e.stopPropagation()}>
                      <FormGroup>
                        <Label>Daily Rate (₹)</Label>
                        <Input
                          type="number"
                          value={form.price}
                          onChange={(e) => set("price", e.target.value)}
                          placeholder="e.g. 1500"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Price Unit</Label>
                        <Select
                          value={form.price_unit}
                          onChange={(e) => set("price_unit", e.target.value as PriceUnit)}
                        >
                          <option value="per_day">Per Day</option>
                          <option value="per_hour">Per Hour</option>
                        </Select>
                      </FormGroup>
                      <FormGroup style={{ gridColumn: "span 2" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                          <div>
                            <Label>Available From</Label>
                            <BrandDatePicker
                              value={form.available_from}
                              onChange={(v) => set("available_from", v)}
                            />
                          </div>
                          <div>
                            <Label>Available To</Label>
                            <BrandDatePicker
                              value={form.available_to}
                              onChange={(v) => set("available_to", v)}
                            />
                          </div>
                        </div>
                        {form.available_from && form.available_to && (
                          <div style={{ marginTop: "12px", fontSize: "13px", color: "var(--mp-featureIconColor, #D863BA)", fontWeight: 600 }}>
                            📅 Listing Duration: {getDaysCount(form.available_from, form.available_to)} Days
                          </div>
                        )}
                      </FormGroup>
                    </OptionFieldsWrap>
                  )}
                </PriceOptionBox>

                {/* Sell Option */}
                <PriceOptionBox 
                  $active={form.listing_type === "sale"}
                  onClick={() => set("listing_type", "sale")}
                >
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <input 
                      type="radio" 
                      checked={form.listing_type === "sale"} 
                      onChange={() => set("listing_type", "sale")}
                      style={{ accentColor: "#c94daa" }}
                    />
                    <strong>📦 Sell Gear</strong>
                  </div>
                  {form.listing_type === "sale" && (
                    <OptionFieldsWrap onClick={e => e.stopPropagation()}>
                      <FormGroup>
                        <Label>Buy Now Price (₹)</Label>
                        <Input
                          type="number"
                          value={form.price}
                          onChange={(e) => set("price", e.target.value)}
                          placeholder="e.g. 85000"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Price Unit</Label>
                        <Select
                          value={form.price_unit}
                          onChange={(e) => set("price_unit", e.target.value as PriceUnit)}
                        >
                          <option value="fixed">Fixed Price</option>
                          <option value="negotiable">Negotiable</option>
                        </Select>
                      </FormGroup>
                    </OptionFieldsWrap>
                  )}
                </PriceOptionBox>
              </PricingTogglesGrid>
            </FormCard>

            {/* Additional Details */}
            <FormCard>
              <CardHeader>
                <CardStepNum>04</CardStepNum>
                <CardSectionName>Additional Details & Visibility</CardSectionName>
              </CardHeader>

              <TwoCol>
                <FormGroup>
                  <Label>Location</Label>
                  <Input
                    value={form.location}
                    onChange={(e) => set("location", e.target.value)}
                    placeholder="e.g. Mumbai, Maharashtra"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Listing Status</Label>
                  <Select
                    value={form.status}
                    onChange={(e) => set("status", e.target.value as ListingStatus)}
                  >
                    <option value="active">Active — Visible publicly</option>
                    <option value="draft">Draft — Hidden</option>
                    <option value="inactive">Inactive — Deactivated</option>
                  </Select>
                </FormGroup>
              </TwoCol>

              <FormGroup>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Describe your equipment details, condition, availability, accessories, etc."
                  rows={5}
                />
              </FormGroup>
            </FormCard>

            {/* Sticky/Fixed Actions Bar */}
            <ActionsRow>
              <ButtonGroup>
                <DraftBtn
                  type="button"
                  disabled={submitting || myProfile?.is_active === false}
                  onClick={(e) => handleSubmit(e, "draft")}
                  style={{
                    opacity: myProfile?.is_active === false ? 0.5 : 1,
                    cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                  }}
                >
                  Save Draft
                </DraftBtn>
                <PublishBtn
                  type="submit"
                  disabled={submitting || imageUploading || myProfile?.is_active === false}
                  style={{
                    opacity: myProfile?.is_active === false ? 0.5 : 1,
                    cursor: myProfile?.is_active === false ? "not-allowed" : "pointer",
                  }}
                >
                  {submitting ? "Saving Listing…" : isEdit ? "Update Listing" : "Publish Listing"}
                </PublishBtn>
              </ButtonGroup>
            </ActionsRow>

          </FormColumn>
        </LayoutGrid>
      </Container>

      {/* Full Size Image Preview Modal / Lightbox */}
      {previewModalIndex !== null && form.image_urls[previewModalIndex] && (
        <LightboxOverlay onClick={closePreview}>
          <LightboxTopBar onClick={(e) => e.stopPropagation()}>
            <LightboxBadge>
              {previewModalIndex === 0 ? (
                <>
                  <Star size={13} fill="#ffc951" color="#ffc951" />
                  <span>#1 Primary Image</span>
                </>
              ) : (
                <span>Photo #{previewModalIndex + 1}</span>
              )}
            </LightboxBadge>
            <LightboxCounter>
              {previewModalIndex + 1} / {form.image_urls.length}
            </LightboxCounter>
            <LightboxCloseBtn onClick={closePreview} title="Close (Esc)">
              <X size={20} />
            </LightboxCloseBtn>
          </LightboxTopBar>

          <LightboxMainArea onClick={(e) => e.stopPropagation()}>
            <LightboxImg
              src={form.image_urls[previewModalIndex]}
              alt={`Preview ${previewModalIndex + 1}`}
            />
          </LightboxMainArea>

          {form.image_urls.length > 1 && (
            <>
              <LightboxNavBtn $left onClick={showPrevPreview} title="Previous photo (←)">
                <ChevronLeft size={28} />
              </LightboxNavBtn>
              <LightboxNavBtn $right onClick={showNextPreview} title="Next photo (→)">
                <ChevronRight size={28} />
              </LightboxNavBtn>
            </>
          )}
        </LightboxOverlay>
      )}
    </PageWrap>
  );
};

export default MarketplaceAddListingPage;

// Styled Components
const PageWrap = styled.div`
  min-height: 100vh;
  background: var(--mp-bodyBackground, #f7f8fc);
  font-family: Urbanist, sans-serif;
  color: var(--mp-textColor, #171717);
`;

const Container = styled.div`
  max-width: 1680px;
  margin: 40px auto;
  padding: 0 40px 80px;

  @media (max-width: 768px) {
    margin: 20px auto;
    padding: 0 12px 60px;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: start;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 2.2fr;
  }
`;

const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: relative;

  @media (min-width: 992px) {
    position: sticky;
    top: 100px;
  }
`;

const FormColumn = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BackBtnWrap = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 700;
  color: var(--mp-heroAccent, #c94daa);
  cursor: pointer;
  width: fit-content;
  padding: 0;
  text-decoration: underline;

  &:hover {
    opacity: 0.85;
  }
`;

const OnboardingLabel = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--mp-heroAccent, #c94daa);
  display: block;
`;

const PageTitle = styled.h2`
  font-size: 26px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
  margin: 0;
  line-height: 1.5;
`;

// Health Widget
const HealthCard = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
`;

const HealthTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
`;

const HealthPercent = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: var(--mp-heroAccent, #c94daa);
`;

const HealthBarOuter = styled.div`
  height: 6px;
  background: var(--mp-bodyBackground, #e5e7eb);
  border-radius: 3px;
  overflow: hidden;
  width: 100%;
`;

const HealthBarInner = styled.div<{ $percent: number }>`
  height: 100%;
  background: var(--mp-heroBtnBg, ${gradient});
  width: ${p => p.$percent}%;
  transition: width 0.4s ease;
`;

const HealthTasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
`;

const TaskItem = styled.div<{ $done: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: ${p => p.$done ? "var(--mp-headingColor, #171717)" : "var(--mp-heroSubColor, rgba(23, 23, 23, 0.45))"};
  font-weight: 600;
  transition: all 0.2s;
`;

const TaskIconWrap = styled.div<{ $done: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${p => p.$done ? "rgba(22, 163, 74, 0.12)" : "var(--mp-bodyBackground, #f3f4f6)"};
  color: ${p => p.$done ? "#16a34a" : "rgba(23, 23, 23, 0.25)"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// Form Card Elements
const FormCard = styled.div`
  background: var(--mp-cardBackground, white);
  border-radius: 16px;
  padding: 32px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.07));
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--mp-border, rgba(23, 23, 23, 0.06));
  margin-bottom: 4px;
`;

const CardStepNum = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.35));
  font-family: Montserrat, sans-serif;
`;

const CardSectionName = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.6));
  font-family: Montserrat, sans-serif;
`;

const Input = styled.input`
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.14));
  border-radius: 10px;
  font-size: 14px;
  font-family: Urbanist, sans-serif;
  color: var(--mp-headingColor, #171717);
  background: var(--mp-bodyBackground, #fafafa);
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: var(--mp-heroAccent, #c94daa);
    background: var(--mp-cardBackground, white);
  }
`;

const Select = styled.select`
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.14));
  border-radius: 10px;
  font-size: 14px;
  font-family: Urbanist, sans-serif;
  color: var(--mp-headingColor, #171717);
  background: var(--mp-bodyBackground, #fafafa);
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: var(--mp-heroAccent, #c94daa);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.14));
  border-radius: 10px;
  font-size: 14px;
  font-family: Urbanist, sans-serif;
  color: var(--mp-headingColor, #171717);
  background: var(--mp-bodyBackground, #fafafa);
  resize: vertical;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: var(--mp-heroAccent, #c94daa);
    background: var(--mp-cardBackground, white);
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

// Image Upload Gallery
const ImageUploadArea = styled.div`
  width: 100%;
`;

const UploadPlaceholderBox = styled.div`
  border: 2.5px dashed var(--mp-border, rgba(201, 77, 170, 0.2));
  background: var(--mp-bodyBackground, #f9fafc);
  border-radius: 16px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;

  &:hover {
    border-color: var(--mp-heroAccent, #c94daa);
    background: var(--mp-cardBackground, white);
  }
`;

const UploadIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(201, 77, 170, 0.1);
  color: var(--mp-heroAccent, #c94daa);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadBoxTitle = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
`;

const UploadBoxSubtitle = styled.span`
  font-size: 11px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.4));
`;

const GalleryUploadGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PrimaryImageSlot = styled.div<{ $isDragging?: boolean; $isDragOver?: boolean }>`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  aspect-ratio: 1.5;
  border: 2px solid ${({ $isDragOver }) => ($isDragOver ? "#c94daa" : "var(--mp-border, rgba(23, 23, 23, 0.08))")};
  cursor: grab;
  user-select: none;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};
  transform: ${({ $isDragOver }) => ($isDragOver ? "scale(1.02)" : "scale(1)")};
  box-shadow: ${({ $isDragOver }) => ($isDragOver ? "0 8px 24px rgba(201, 77, 170, 0.25)" : "none")};
  transition: all 0.2s ease;

  &:active {
    cursor: grabbing;
  }
`;

const SecondarySlotsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
`;

const SecondaryImageSlot = styled.div<{ $isDragging?: boolean; $isDragOver?: boolean }>`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 1.1;
  border: 2px solid ${({ $isDragOver }) => ($isDragOver ? "#c94daa" : "var(--mp-border, rgba(23, 23, 23, 0.08))")};
  cursor: grab;
  user-select: none;
  min-width: 0;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};
  transform: ${({ $isDragOver }) => ($isDragOver ? "scale(1.03)" : "scale(1)")};
  box-shadow: ${({ $isDragOver }) => ($isDragOver ? "0 6px 18px rgba(201, 77, 170, 0.2)" : "none")};
  transition: all 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    border-color: #c94daa;
  }
`;

const SecondaryUploadPlaceholder = styled.div`
  border: 1.5px dashed var(--mp-border, rgba(23, 23, 23, 0.15));
  background: var(--mp-bodyBackground, #fafafa);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  width: 100%;
  aspect-ratio: 1.1;
  font-size: 11px;
  font-weight: 700;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.45));
  transition: all 0.2s;
  min-width: 0;

  &:hover {
    border-color: var(--mp-heroAccent, #c94daa);
    color: var(--mp-heroAccent, #c94daa);
  }
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveImgBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  z-index: 2;

  &:hover {
    background: #dc2626;
  }
`;

const PrimaryBadge = styled.span`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: linear-gradient(135deg, #ffc951 0%, #c94daa 100%);
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  z-index: 2;
  pointer-events: none;
`;

const NumberBadge = styled.span`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(23, 23, 23, 0.78);
  color: #ffffff;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  backdrop-filter: blur(4px);
  display: inline-flex;
  align-items: center;
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  pointer-events: none;
`;

const GalleryHintText = styled.p`
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--mp-heroSubColor, #555869);
  display: flex;
  align-items: center;
  gap: 6px;

  strong {
    color: var(--mp-headingColor, #171717);
  }
`;

// Lightbox Preview Styles
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(244, 246, 250, 0.94);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  z-index: 999999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px 32px;
  box-sizing: border-box;
  user-select: none;
  animation: ${fadeUp} 0.2s ease-out;

  [data-marketplace-theme="dark"] & {
    background: rgba(8, 9, 11, 0.95);
  }
`;

const LightboxTopBar = styled.div`
  position: absolute;
  top: 20px;
  left: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  z-index: 1000;
  pointer-events: auto;
`;

const LightboxBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.92);
  border: 1.5px solid rgba(23, 23, 23, 0.1);
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  color: #171717;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);

  [data-marketplace-theme="dark"] & {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }
`;

const LightboxCounter = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #171717;
  background: rgba(255, 255, 255, 0.92);
  padding: 6px 16px;
  border-radius: 999px;
  border: 1.5px solid rgba(23, 23, 23, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);

  [data-marketplace-theme="dark"] & {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }
`;

const LightboxCloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.92);
  border: 1.5px solid rgba(23, 23, 23, 0.1);
  color: #171717;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  [data-marketplace-theme="dark"] & {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.25);
    color: #ffffff;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  }

  &:hover {
    background: #ef4444;
    border-color: #ef4444;
    color: #ffffff;
    transform: scale(1.1);
  }
`;

const LightboxMainArea = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  max-width: 1200px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  min-height: 0;
`;

const LightboxImg = styled.img`
  width: min(85vw, 1000px);
  height: min(72vh, 720px);
  object-fit: contain;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeUp} 0.2s ease-out;
  display: block;

  [data-marketplace-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.85);
  }

  @media (max-width: 768px) {
    width: 92vw;
    height: 65vh;
  }
`;

const LightboxNavBtn = styled.button<{ $left?: boolean; $right?: boolean }>`
  position: absolute;
  top: 50%;
  ${({ $left }) => ($left ? "left: 24px;" : "")}
  ${({ $right }) => ($right ? "right: 24px;" : "")}
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1.5px solid rgba(23, 23, 23, 0.12);
  color: #171717;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.2s;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);

  [data-marketplace-theme="dark"] & {
    background: rgba(18, 20, 24, 0.8);
    border-color: rgba(255, 255, 255, 0.22);
    color: #ffffff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.6);
  }

  &:hover {
    background: #c94daa;
    border-color: #c94daa;
    color: #ffffff;
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 42px;
    height: 42px;
    ${({ $left }) => ($left ? "left: 10px;" : "")}
    ${({ $right }) => ($right ? "right: 10px;" : "")}
  }
`;

// Pricing Card Elements
const PricingTogglesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PriceOptionBox = styled.div<{ $active: boolean }>`
  border: 2px solid ${p => p.$active ? "#c94daa" : "var(--mp-border, rgba(23, 23, 23, 0.08))"};
  background: var(--mp-cardBackground, white);
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: all 0.2s;

  ${p => p.$active && `
    background: rgba(201, 77, 170, 0.04);
    box-shadow: 0 8px 20px rgba(201, 77, 170, 0.06);

    [data-marketplace-theme="dark"] & {
      background: rgba(201, 77, 170, 0.15);
      box-shadow: 0 8px 24px rgba(201, 77, 170, 0.25);
    }
  `}

  strong {
    font-size: 14px;
    font-weight: 800;
    color: var(--mp-headingColor, #171717);
  }
`;

const OptionFieldsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 4px;
  animation: ${fadeUp} 0.2s ease;
`;

// Footer actions row
const ActionsRow = styled.div`
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.07));
  border-radius: 16px;
  padding: 20px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const ProtectNotice = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.55));
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: flex-end;

  @media (max-width: 600px) {
    button {
      flex: 1;
    }
  }
`;

const DraftBtn = styled.button`
  height: 44px;
  padding: 0 24px;
  background: var(--mp-bodyBackground, #f3f4f6);
  color: var(--mp-headingColor, #171717);
  border: 1px solid var(--mp-border, rgba(23,23,23,0.1));
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--mp-cardBackground, white);
    border-color: #c94daa;
  }
`;

const PublishBtn = styled.button`
  height: 44px;
  padding: 0 28px;
  background: var(--mp-heroBtnBg, ${gradient});
  color: var(--mp-heroBtnColor, white);
  border: none;
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.2);
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 120px 20px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.4));
`;

const LoadingText = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--mp-heroAccent, #c94daa);
  margin-top: 8px;
`;
